/**
 * Conexión de los formularios de VCARS con el Web App de Google Apps Script.
 *
 * Endpoint activo: SHEETS_ENDPOINT (POST + JSON).
 * Cada envío incluye "tipoFormulario" para que el script sepa en qué hoja escribir:
 *   consulta → Consultas · venta → Venta de autos
 *   financiacion → Financiación · permuta → Permutas
 *
 * El ID, la fecha/hora y el estado inicial los genera el script en Google.
 * Email de aviso: se configura en EMAIL_DESTINO dentro del script
 * (ver docs/google-apps-script-vcars.md).
 */
export const SHEETS_ENDPOINT =
  'https://script.google.com/macros/s/AKfycbxeLZyUN_BP6BaOGQ5XV-KMwwsuzyDSHz9GeFgQNfPf8hJJuoU18cEKBLE_6uOBBn0Qsg/exec'

export const SPREADSHEET_URL =
  'https://docs.google.com/spreadsheets/d/1zg4BJxd_A_7py4qV4gZYnfxhS1r-G7MRUG0re0GOgPk/edit'

export type SheetKey =
  | 'consulta'
  | 'venta'
  | 'financiacion'
  | 'permuta'
  | 'simulacion'
  | 'financiacionUsado'

type Row = Record<string, string>

const value = (d: Row, key: string) => (d[key] ?? '').trim()

/** Arma un texto legible "Etiqueta: valor" por línea, salteando los vacíos. */
const joinLines = (pairs: [string, string][]) =>
  pairs
    .filter(([, v]) => v)
    .map(([label, v]) => `${label}: ${v}`)
    .join('\n')

/** Arma el JSON exacto que espera el endpoint para cada formulario. */
const PAYLOAD_BUILDERS: Record<SheetKey, (d: Row) => Record<string, string>> = {
  consulta: (d) => ({
    tipoFormulario: 'consulta',
    nombre: value(d, 'nombre'),
    telefono: value(d, 'telefono'),
    email: value(d, 'email'),
    motivo: value(d, 'motivo'),
    vehiculoInteres: value(d, 'vehiculoInteres'),
    mensaje: value(d, 'mensaje'),
  }),
  venta: (d) => ({
    tipoFormulario: 'venta',
    nombre: value(d, 'nombre'),
    telefono: value(d, 'telefono'),
    email: value(d, 'email'),
    marca: value(d, 'marca'),
    modelo: value(d, 'modelo'),
    version: value(d, 'version'),
    anio: value(d, 'anio'),
    kilometraje: value(d, 'kilometraje'),
    precioPretendido: value(d, 'precioPretendido'),
    ubicacion: value(d, 'ubicacion'),
    aceptaPermuta: value(d, 'aceptaPermuta'),
    estadoGeneral: value(d, 'estadoGeneral'),
    comentarios: value(d, 'comentarios'),
    fotos: value(d, 'fotos'),
  }),
  financiacion: (d) => ({
    tipoFormulario: 'financiacion',
    nombre: value(d, 'nombre'),
    telefono: value(d, 'telefono'),
    email: value(d, 'email'),
    vehiculoInteres: value(d, 'vehiculo'),
    presupuestoEstimado: value(d, 'presupuesto'),
    tieneUsado: value(d, 'permutaParteDePago'),
    mensaje: value(d, 'mensaje'),
  }),
  permuta: (d) => ({
    tipoFormulario: 'permuta',
    nombre: value(d, 'nombre'),
    telefono: value(d, 'telefono'),
    marca: value(d, 'marca'),
    modelo: value(d, 'modelo'),
    anio: value(d, 'anio'),
    kilometraje: value(d, 'kilometraje'),
    estadoGeneral: value(d, 'estadoGeneral'),
    vehiculoInteres: value(d, 'vehiculoInteres'),
    mensaje: joinLines([
      ['Monto estimado por su vehículo', value(d, 'montoEstimado')],
      ['Versión', value(d, 'version')],
      ['Comentarios', value(d, 'mensaje')],
    ]),
  }),
  /**
   * Financiación de una unidad usada del catálogo. Va a la misma hoja
   * "Financiación"; el detalle específico viaja en "mensaje".
   */
  financiacionUsado: (d) => ({
    tipoFormulario: 'financiacion',
    nombre: value(d, 'nombre'),
    telefono: value(d, 'telefono'),
    email: value(d, 'email'),
    vehiculoInteres: value(d, 'vehiculoInteres'),
    presupuestoEstimado: value(d, 'anticipo'),
    tieneUsado: value(d, 'permutaParteDePago'),
    mensaje: joinLines([
      ['Tipo de operación', 'Financiación de usado seleccionado'],
      ['CUIL', value(d, 'cuil')],
      ['Anticipo disponible', value(d, 'anticipo')],
      ['Monto a financiar', value(d, 'montoFinanciar')],
      ['Cuotas deseadas', value(d, 'cuotas')],
      ['Entrega usado en parte de pago', value(d, 'permutaParteDePago')],
      ['Cliente activo bancario', value(d, 'clienteBancario')],
      ['Recibo de sueldo o monotributo', value(d, 'ingresos')],
      ['Comentarios', value(d, 'mensaje')],
    ]),
  }),
  /**
   * Simulación de crédito: usa la hoja "Financiación" (no hace falta tocar el
   * Apps Script). Las respuestas específicas viajan ordenadas en "mensaje".
   */
  simulacion: (d) => ({
    tipoFormulario: 'financiacion',
    nombre: value(d, 'nombre'),
    telefono: value(d, 'telefono'),
    email: value(d, 'email'),
    vehiculoInteres:
      value(d, 'vehiculoInteres') || 'Simulación de crédito hasta el 100%',
    presupuestoEstimado: value(d, 'anticipoPermuta'),
    tieneUsado: value(d, 'anticipoPermuta'),
    mensaje: joinLines([
      ['CUIL', value(d, 'cuil')],
      ['Tiene anticipo o permuta', value(d, 'anticipoPermuta')],
      ['Cliente activo bancario', value(d, 'clienteBancario')],
      ['Quiere simular crédito hasta el 100%', value(d, 'simular100')],
      ['Cuenta con garante si es rechazado', value(d, 'garante')],
      ['Desea que VCARS lo contacte si aprueba', value(d, 'continuar')],
      ['Comentarios', value(d, 'mensaje')],
    ]),
  }),
}

export function formDataToRecord(form: HTMLFormElement): Row {
  const entries = Array.from(new FormData(form).entries())
  const record: Row = {}
  entries.forEach(([key, entry]) => {
    if (entry instanceof File) {
      if (!entry.name) return
      record[key] = record[key] ? `${record[key]}, ${entry.name}` : entry.name
      return
    }
    record[key] = String(entry)
  })
  return record
}

export interface SubmitResult {
  ok: boolean
  /** ID devuelto por Apps Script, cuando la respuesta puede leerse. */
  id?: string
}

/**
 * POST en JSON al Web App. Crea una fila nueva; nunca sobrescribe.
 *
 * Apps Script no responde el preflight de CORS, así que se envía como "simple request":
 * Content-Type text/plain y cuerpo JSON.stringify(payload). Si el navegador igual
 * bloquea la lectura de la respuesta, se reintenta en mode: 'no-cors' (respuesta opaca:
 * no se lee el body y se considera enviado si el fetch no lanza error).
 */
export async function submitLead(
  key: SheetKey,
  data: Row,
): Promise<SubmitResult> {
  const payload = PAYLOAD_BUILDERS[key](data)
  const body = JSON.stringify(payload)

  try {
    const response = await fetch(SHEETS_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body,
      redirect: 'follow',
    })

    let id: string | undefined
    try {
      const text = await response.text()
      const parsed = text ? JSON.parse(text) : null
      if (parsed && typeof parsed.id === 'string') id = parsed.id
    } catch {
      // Respuesta no legible (redirect de Google, cuerpo vacío): el POST igual llegó.
    }

    return { ok: true, id }
  } catch (error) {
    console.warn('[VCARS] Fetch con CORS bloqueado, reintentando en no-cors', error)

    try {
      // Respuesta opaca: no se puede leer, solo confirmar que se envió.
      await fetch(SHEETS_ENDPOINT, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body,
      })
      return { ok: true }
    } catch (fallbackError) {
      console.error(
        '[VCARS] No se pudo enviar el formulario a Google Sheets',
        fallbackError,
      )
      return { ok: false }
    }
  }
}
